import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import { useHeaderHeight } from '@react-navigation/elements';
import {
  BodyText,
  Button,
  HStack,
  IllusSeasonal,
  Image,
  theme,
  TitleText,
  useResponsive,
} from '@sg/garnish';

export const DeliveryOrderInFlightContent = ({
  onClose,
  onOrderStatus,
}: DeliveryOrderInFlightContentProps) => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const paddingStyle = useHeaderCompensationPadding();

  return (
    <View
      style={[
        styles.container,
        currentBreakpoint.isXS && styles.justifyCenter,
        currentBreakpoint.isXS && paddingStyle,
      ]}
    >
      <View style={styles.alignCenter}>
        {currentBreakpoint.isXS ? (
          <Image
            source={IllusSeasonal}
            style={[styles.image, styles.imageWrapperXS]}
          />
        ) : null}

        <View style={styles.row}>
          {currentBreakpoint.isXS ? null : (
            <Image
              source={IllusSeasonal}
              style={[styles.image, styles.imageWrapperLG]}
            />
          )}

          <View style={styles.info}>
            <TitleText style={styles.title} size={5}>
              {formatMessage(messages.deliveryInFlightModalTitle)}
            </TitleText>
            <BodyText>
              {formatMessage(messages.deliveryInFlightModalSubtitle)}
            </BodyText>
          </View>
        </View>
      </View>

      <View
        style={currentBreakpoint.isXS ? styles.buttonsXS : styles.buttonsLg}
      >
        <HStack itemsPerRow={2}>
          <Button
            size="large"
            testID="delivery-order-in-flight.close"
            palette="secondary"
            onPress={onClose}
          >
            {formatMessage(messages.deliveryInFlightModalCtaClose)}
          </Button>
          <Button
            size="large"
            testID="delivery-order-in-flight.order-status"
            onPress={onOrderStatus}
          >
            {formatMessage(messages.deliveryInFlightModalCtaStatus)}
          </Button>
        </HStack>
      </View>
    </View>
  );
};

const useHeaderCompensationPadding = () => {
  const headerHeight = useHeaderHeight();

  return useStyle(() => ({ paddingBottom: headerHeight }), [headerHeight]);
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  deliveryInFlightModalTitle: {
    defaultMessage: 'You already have an order on the way',
    description: 'Delivery | In Flight Modal | Title',
  },
  deliveryInFlightModalSubtitle: {
    defaultMessage: `You cannot place a delivery order when there's an order currently in flight. Sit tight and enjoy your sweetgreen.`,
    description: 'Delivery | In Flight Modal | Subtitle',
  },
  deliveryInFlightModalCtaClose: {
    defaultMessage: 'Close',
    description: 'Delivery | In Flight Modal | Cta Close',
  },
  deliveryInFlightModalCtaStatus: {
    defaultMessage: 'Order status',
    description: 'Delivery | In Flight Modal | Cta Status',
  },
});

// ─── Types ────────────────────────────────────────────────────────

type DeliveryOrderInFlightContentProps = Readonly<{
  onClose: () => void;
  onOrderStatus: () => void;
}>;

// ─── Styles ───────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: theme.spacing['6'],
  },
  buttonsXS: {
    marginTop: theme.spacing['6'],
  },
  buttonsLg: {
    marginTop: theme.spacing['4'],
  },
  info: {
    flex: 1,
  },
  alignCenter: {
    alignItems: 'center',
  },
  justifyCenter: {
    justifyContent: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    marginBottom: theme.spacing['2'],
  },
  image: {
    width: 200,
    height: 200,
  },
  imageWrapperXS: {
    marginBottom: theme.spacing['6'],
  },
  imageWrapperLG: {
    marginRight: theme.spacing['4'],
  },
});
